import { useEffect } from 'react';

/**
 * Analytics component that loads Google Tag Manager (GTM) for production only.
 * 
 * GTM should be configured to load:
 * - Google Analytics (GA4): G-P657FLD845
 * - Microsoft Clarity: v933lutmlv
 * 
 * This component is included in Layout, so it only runs on public pages (not admin).
 * 
 * Performance optimization: Defers loading until after initial render and idle time.
 */
export default function Analytics() {
  const gtmId = import.meta.env['VITE_GTM_ID'];

  useEffect(() => {
    // Only load in production and if GTM ID is configured
    if (!gtmId || import.meta.env['DEV']) {
      return;
    }

    // Check if GTM is already loaded (prevent duplicate loading)
    if (window.dataLayer) {
      return;
    }

    // Defer analytics loading to not block main thread during initial render
    const loadAnalytics = () => {
      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });

      // Load GTM script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      document.head.appendChild(script);

      // Add noscript iframe to body
      const noscript = document.createElement('noscript');
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    };

    // Use requestIdleCallback to defer loading until browser is idle
    // Falls back to setTimeout for browsers that don't support it
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(loadAnalytics, { timeout: 3000 });
      return () => window.cancelIdleCallback(idleId);
    } else {
      // Fallback: defer by 2 seconds to allow LCP to complete
      const timeoutId = setTimeout(loadAnalytics, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [gtmId]);

  // This component doesn't render anything
  return null;
}

// TypeScript declaration for dataLayer
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}
